import React from 'react';
import { CalendarDays, Clock, Coffee, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const STATUS_STYLES = {
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  scheduled: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
};

const prettyStatus = (s) => (s || '').replace(/_/g, ' ');

export const hoursLabel = (minutes) => {
  if (!minutes) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h ? `${h}h ` : ''}${m ? `${m}m` : ''}`.trim();
};

export const ScheduleDayBody = ({ day, loading, error, newIds = [] }) => {
  const { t } = useTranslation();
  const summary = day?.summary;

  return (
    <>
      {summary && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="schedule-summary">
          {[
            { label: t('schedule.visits', 'Bookings'), value: summary.total_bookings, icon: Users, testid: 'summary-bookings' },
            { label: t('schedule.booked', 'Booked'), value: hoursLabel(summary.booked_minutes), icon: Clock, testid: 'summary-booked' },
            { label: t('schedule.freeTime', 'Free time'), value: hoursLabel(summary.free_minutes), icon: Coffee, testid: 'summary-free' },
            { label: t('schedule.utilisation', 'Utilisation'), value: `${summary.utilisation_pct}%`, icon: CalendarDays, testid: 'summary-utilisation' },
          ].map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={card.testid}>
                <Icon className="h-4 w-4 text-slate-400" />
                <p className="mt-2 text-xl font-bold text-slate-900">{card.value}</p>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{card.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {day && !day.is_day_off && day.working_hours?.start && (
        <p className="numeric-ltr mt-4 text-sm text-slate-500" data-testid="schedule-working-hours">
          {t('schedule.workingFromTo', 'Working {{start}}–{{end}}', { start: day.working_hours.start, end: day.working_hours.end })}
          {day.working_hours.break_start
            ? ` · ${t('schedule.breakFromTo', 'break {{start}}–{{end}}', { start: day.working_hours.break_start, end: day.working_hours.break_end })}`
            : ''}
          {summary?.next_appointment_time ? ` · ${t('schedule.nextUp', 'next up {{time}}', { time: summary.next_appointment_time })}` : ''}
        </p>
      )}

      <div className="mt-4 space-y-3">
        {loading && (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="schedule-loading">
            {t('schedule.loadingSchedule', 'Loading schedule…')}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm font-semibold text-rose-700" data-testid="schedule-error">
            {error}
          </div>
        )}

        {!loading && !error && day?.is_day_off && (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center" data-testid="schedule-day-off">
            <Coffee className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="font-bold text-slate-900">{t('schedule.dayOff', 'Day off')}</p>
            <p className="mt-1 text-sm text-slate-500">{day.day_off_reason || t('schedule.notScheduled', 'Not scheduled to work today.')}</p>
          </div>
        )}

        {!loading && !error && !day?.is_day_off && day?.appointments?.length === 0 && (
          <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="schedule-empty">
            <CalendarDays className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="font-bold text-slate-900">{t('schedule.noBookings', 'No bookings yet')}</p>
            <p className="mt-1 text-sm text-slate-500">
              {t('schedule.noBookingsHint', 'This page updates on its own — new bookings appear here within seconds.')}
            </p>
          </div>
        )}

        {!loading && !error && (day?.appointments || []).map((apt) => (
          <div
            key={apt.id}
            data-testid={`schedule-appointment-${apt.id}`}
            className={`flex gap-4 rounded-2xl border bg-white p-4 transition-colors ${
              newIds.includes(apt.id) ? 'border-emerald-400 ring-2 ring-emerald-200' : 'border-slate-200'
            }`}
          >
            <div className="w-20 shrink-0 border-e border-slate-100 pe-3 text-center">
              <p className="numeric-ltr text-lg font-bold leading-tight text-slate-900">{apt.start_time}</p>
              <p className="numeric-ltr text-xs text-slate-400">{apt.end_time}</p>
              <p className="numeric-ltr mt-1 text-[11px] font-semibold text-slate-400">{apt.duration} {t('schedule.minutes', 'min')}</p>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate font-bold text-slate-900">{apt.service_name}</p>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                  STATUS_STYLES[apt.status] || 'bg-slate-100 text-slate-600 border-slate-200'
                }`}>
                  {prettyStatus(apt.status)}
                </span>
                {newIds.includes(apt.id) && (
                  <span
                    data-testid={`schedule-new-badge-${apt.id}`}
                    className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                  >
                    {t('schedule.newBadge', 'New')}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-slate-600" data-testid={`schedule-customer-${apt.id}`}>
                {apt.customer_first_name}
              </p>
              {apt.reference_number && (
                <p className="mt-1 font-mono text-[11px] text-slate-400">{apt.reference_number}</p>
              )}
            </div>
          </div>
        ))}

        {!loading && !error && (day?.gaps || []).length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="schedule-gaps">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('schedule.openSlots', 'Open slots')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {day.gaps.map((gap) => (
                <span
                  key={`${gap.start_time}-${gap.end_time}`}
                  className="numeric-ltr rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600"
                >
                  {gap.start_time}–{gap.end_time} · {hoursLabel(gap.minutes)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ScheduleDayBody;
